/**
 * Cliente HTTP tipado para a API.
 * Single source of truth para chamadas ao backend.
 *
 * Adiciona Authorization: Bearer <access_token> em chamadas autenticadas.
 * Token vem do MSAL.js — pega silently com refresh automático.
 */

import type { Proposta, TabelaPrecosCompleta, TabelaPrecosInput } from '@infomatic/shared';

import { getAccessToken, isAuthenticated } from './msal.js';

// Em produção, o web aponta para a Function App standalone via variável
// injetada no build (VITE_API_BASE_URL). Em dev local, usa o proxy do Vite.
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
}

interface RequestOptions extends RequestInit {
  /** Se true, não adiciona Authorization header (pra rotas públicas) */
  skipAuth?: boolean;
}

async function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }

  if (!init.skipAuth && isAuthenticated()) {
    try {
      const token = await getAccessToken();
      headers.set('Authorization', `Bearer ${token}`);
    } catch {
      // sem token — backend responde 401 se exigir
    }
  }

  const response = await fetch(url, { ...init, headers, credentials: 'include' });

  if (!response.ok) {
    let body: ApiErrorBody = {};
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      // body não é JSON
    }
    throw new ApiError(
      body.error?.message ?? response.statusText,
      response.status,
      body.error?.code,
      body.error?.details,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

// ── Health ──────────────────────────────────────────────────────────

export interface HealthResponse {
  status: 'ok' | 'degraded';
  env: string;
  version: string;
  checks: Record<string, 'ok' | 'fail' | 'unknown'>;
  ts: string;
  durationMs: number;
}

export interface Cliente {
  empresa: string;
  cnpj?: string;
  cpf?: string;
  responsavel: string;
  cargo?: string;
  email: string;
  telefone?: string;
  endereco?: {
    rua: string;
    numero: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    uf: string;
    cep: string;
  };
  segmento?: string;
}

export interface ContatosContratoAPI {
  assinaturaEmail: string;
  financeiro: { nome: string; email: string };
  notaFiscalEmail?: string;
  implantacao?: { nome: string; email: string; telefone?: string };
  preenchidoEm: string;
}

export interface DadosContratoAPI {
  /** Razão social (PJ) ou Nome completo (PF) */
  razaoSocial: string;
  /** CNPJ (PJ) — 14 dígitos. Um de cnpj ou cpf é obrigatório */
  cnpj?: string;
  /** CPF (PF) — 11 dígitos */
  cpf?: string;
  inscricaoEstadual?: string;
  inscricaoMunicipal?: string;
  endereco: {
    rua: string;
    numero: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    uf: string;
    cep: string;
  };
  responsavelLegal: {
    nome: string;
    cpf: string;
    cargo: string;
    email: string;
    telefone: string;
  };
  diaVencimento: 15 | 20 | 25;
  preenchidoEm: string;
}

export const api = {
  health: (): Promise<HealthResponse> => request<HealthResponse>('/health', { skipAuth: true }),

  // ── Propostas (admin, autenticado) ───────────────────────────────
  propostas: {
    list: (filters?: {
      status?: string;
      vendedor?: string;
      empresa?: string;
      continuation?: string;
      pageSize?: number;
    }): Promise<{ items: Proposta[]; continuation?: string; count: number }> => {
      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      if (filters?.vendedor) params.set('vendedor', filters.vendedor);
      if (filters?.empresa) params.set('empresa', filters.empresa);
      if (filters?.continuation) params.set('continuation', filters.continuation);
      if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
      const qs = params.toString();
      return request(`/propostas${qs ? `?${qs}` : ''}`);
    },
    get: (id: string): Promise<{ proposta: Proposta }> =>
      request(`/propostas/${encodeURIComponent(id)}`),
    /**
     * Envia o contrato pro Clicksign — gera .docx + upload + signer + notifica.
     * CUIDADO: produção consome créditos Clicksign por chamada.
     */
    enviarClicksign: (
      id: string,
      body?: { mensagem?: string },
    ): Promise<{
      proposta: { id: string; numero: string; status: string };
      assinatura: {
        provedor: string;
        documentoId: string;
        url: string;
        status: string;
        assinantes: Array<{ nome: string; email: string; status: string }>;
        enviadoEm: string;
      };
    }> =>
      request(`/propostas/${encodeURIComponent(id)}/enviar-clicksign`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }),
    /**
     * Vincula um documento Clicksign existente à proposta (recuperação manual).
     * Usado quando documento foi criado no Clicksign mas a proposta local não foi atualizada.
     */
    vincularClicksign: (
      id: string,
      documentKey: string,
    ): Promise<{
      proposta: { id: string; numero: string; status: string };
      assinatura: {
        provedor: string;
        documentoId: string;
        url: string;
        status: string;
      };
    }> =>
      request(`/propostas/${encodeURIComponent(id)}/vincular-clicksign`, {
        method: 'POST',
        body: JSON.stringify({ documentKey }),
      }),
    /**
     * Faz polling no Clicksign pra atualizar status da assinatura.
     * Manual enquanto webhook não está configurado.
     */
    atualizarAssinatura: (
      id: string,
    ): Promise<{
      proposta: { id: string; numero: string; status: string };
      assinatura: {
        status: string;
        assinantes: Array<{ nome: string; email: string; status: string; assinadoEm?: string }>;
        completadoEm?: string;
        pdfBlobUrl?: string;
      };
      mudou: boolean;
    }> =>
      request(`/propostas/${encodeURIComponent(id)}/atualizar-assinatura`, {
        method: 'POST',
      }),
    /** Reenvia o e-mail de assinatura pro signatário (envelope pendente, sem gerar novo). */
    reenviarAssinatura: (
      id: string,
    ): Promise<{ proposta: { id: string; numero: string; status: string } }> =>
      request(`/propostas/${encodeURIComponent(id)}/reenviar-assinatura`, {
        method: 'POST',
      }),
    /** Reenvia o e-mail pro gestor de implantação (#238). */
    reenviarEmailImplantacao: (
      id: string,
    ): Promise<{ proposta: { id: string; numero: string; status: string } }> =>
      request(`/propostas/${encodeURIComponent(id)}/reenviar-email-implantacao`, {
        method: 'POST',
      }),
    /**
     * Baixa o .docx do contrato preenchido (gerado server-side).
     * Backend retorna binário (não JSON), por isso usa fetch direto.
     */
    baixarContrato: async (id: string): Promise<Blob> => {
      const headers = new Headers({ Accept: 'application/octet-stream' });
      if (isAuthenticated()) {
        try {
          const token = await getAccessToken();
          headers.set('Authorization', `Bearer ${token}`);
        } catch {
          // sem token — backend responde 401 se exigir
        }
      }
      const url = `${API_BASE}/propostas/${encodeURIComponent(id)}/contrato`;
      const resp = await fetch(url, { headers, credentials: 'include' });
      if (!resp.ok) {
        let body: ApiErrorBody = {};
        try {
          body = (await resp.json()) as ApiErrorBody;
        } catch {
          // body não é JSON
        }
        throw new ApiError(
          body.error?.message ?? resp.statusText,
          resp.status,
          body.error?.code,
          body.error?.details,
        );
      }
      return resp.blob();
    },
    create: (body: unknown): Promise<{ proposta: Proposta }> =>
      request('/propostas', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, patch: unknown): Promise<{ proposta: Proposta }> =>
      request(`/propostas/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    mudarStatus: (
      id: string,
      novoStatus: string,
      motivo?: string,
    ): Promise<{ proposta: Proposta }> =>
      request(`/propostas/${encodeURIComponent(id)}/status`, {
        method: 'POST',
        body: JSON.stringify({ novoStatus, motivo }),
      }),
    gerarLink: (
      id: string,
      baseUrl?: string,
      preview?: boolean,
    ): Promise<{ token: string; url: string; expiraEm: string }> =>
      request(`/propostas/${encodeURIComponent(id)}/link`, {
        method: 'POST',
        body: JSON.stringify({
          ...(baseUrl ? { baseUrl } : {}),
          ...(preview ? { preview: true } : {}),
        }),
      }),
    enviar: (
      id: string,
      baseUrl?: string,
    ): Promise<{
      proposta: Proposta;
      linkUrl: string;
      emailOk: boolean;
      emailReason?: string;
    }> =>
      request(`/propostas/${encodeURIComponent(id)}/enviar`, {
        method: 'POST',
        body: JSON.stringify(baseUrl ? { baseUrl } : {}),
      }),
    timeline: (
      id: string,
    ): Promise<{
      propostaId: string;
      statusAtual: string;
      etapas: { key: string; label: string; completadaEm: string | null }[];
      terminal: {
        tipo: 'perdida' | 'cancelada';
        label: string;
        em: string;
        motivo?: string;
      } | null;
    }> => request(`/propostas/${encodeURIComponent(id)}/timeline`),
  },

  // ── Clientes (lookup Omie, autenticado) ──────────────────────────
  clientes: {
    lookupOmie: (cnpj: string): Promise<{ cliente: Cliente }> =>
      request(`/clientes/omie?cnpj=${encodeURIComponent(cnpj)}`),
    buscarProdutos: (
      q: string,
    ): Promise<{
      produtos: {
        id: string;
        codigo: string;
        descricao: string;
        unidade: string;
        valorUnitario: number;
        estoqueFisico: number;
        estoqueReservado: number;
        estoqueDisponivel: number;
      }[];
    }> => request(`/clientes/omie/produtos?q=${encodeURIComponent(q)}`),
    buscarServicos: (
      q: string,
    ): Promise<{
      servicos: { id: string; codigo: string; descricao: string; valorUnitario: number }[];
    }> => request(`/clientes/omie/servicos?q=${encodeURIComponent(q)}`),
  },

  // ── Contratos (Omie, autenticado) ────────────────────────────────
  contratos: {
    /**
     * Lista contratos Omie ATIVOS vencendo nos próximos N dias.
     * Usado no bloco "Renovações próximas" do dashboard (#226).
     */
    vencendo: (
      dias = 90,
      incluirVencidos = false,
    ): Promise<{
      dias: number;
      total: number;
      contratos: Array<{
        codigoOmie: number;
        numero: string;
        situacao: string;
        dataInicio: string;
        dataTermino: string;
        diasRestantes: number;
        valorMensal: number;
        cliente: { codigoOmie: number; empresa: string; cnpj: string; email: string };
      }>;
    }> =>
      request(
        `/contratos/vencendo?dias=${encodeURIComponent(String(dias))}&incluirVencidos=${incluirVencidos}`,
      ),
  },

  // ── Admin: sync catalogo Omie ────────────────────────────────────
  admin: {
    omieSyncStatus: (): Promise<{
      catalogo: {
        produtos: {
          total: number;
          ultimoSyncEm: string | null;
          status: 'ok' | 'throttled' | 'error' | 'never';
          erro?: string;
        };
        servicos: {
          total: number;
          ultimoSyncEm: string | null;
          status: 'ok' | 'throttled' | 'error' | 'never';
          erro?: string;
        };
      };
    }> => request(`/config/omie/sync`),
    omieSyncTrigger: (): Promise<{
      catalogo: {
        produtos: {
          total: number;
          ultimoSyncEm: string | null;
          status: 'ok' | 'throttled' | 'error' | 'never';
          erro?: string;
        };
        servicos: {
          total: number;
          ultimoSyncEm: string | null;
          status: 'ok' | 'throttled' | 'error' | 'never';
          erro?: string;
        };
      };
    }> => request(`/config/omie/sync`, { method: 'POST' }),

    /**
     * Health check do Clicksign (#263) — valida que o token do Key Vault
     * ainda é aceito. Usado no badge "Clicksign" do dashboard.
     */
    healthClicksign: (): Promise<{
      status: 'ok' | 'fail';
      motivo?: 'token-invalido' | 'inacessivel' | 'erro-http';
      httpStatus?: number;
      baseUrl: string;
      env: string;
      durationMs: number;
      ts: string;
    }> => request(`/config/health/clicksign`),

    precosGet: (): Promise<{ tabela: TabelaPrecosCompleta }> => request(`/config/precos`),

    precosSave: (input: TabelaPrecosInput): Promise<{ tabela: TabelaPrecosCompleta }> =>
      request(`/config/precos`, { method: 'PUT', body: JSON.stringify(input) }),
    precosVersoes: (): Promise<{ versoes: TabelaPrecosCompleta[] }> =>
      request(`/config/precos/versoes`),
    /**
     * Histórico de alterações dos valores de onboarding.
     * Lê do container audit eventos do tipo `precos.onboarding.atualizado`.
     */
    onboardingLog: (): Promise<{
      eventos: Array<{
        timestamp: string;
        usuarioEmail: string;
        diffs: Array<{ campo: string; anterior: number; novo: number }>;
      }>;
    }> => request(`/config/precos/onboarding-log`),
  },

  // ── View pública (cliente, sem auth) ─────────────────────────────
  public: {
    getProposta: (
      token: string,
    ): Promise<{
      proposta: Omit<Proposta, 'pricing'>;
      fillContractToken: string | null;
      fillContractExpiraEm: string | null;
    }> => request(`/public/propostas/${encodeURIComponent(token)}`, { skipAuth: true }),
    aceitar: (
      token: string,
      body: { nome: string; email: string; cargo?: string },
    ): Promise<{
      jaAceita: boolean;
      numero: string;
      status: string;
      aceite: {
        nome: string;
        email: string;
        cargo?: string;
        ip: string;
        userAgent: string;
        timestamp: string;
        jti: string;
      };
      contatosContrato: ContatosContratoAPI | null;
      fillContractToken: string | null;
      fillContractExpiraEm: string | null;
    }> =>
      request(`/public/propostas/${encodeURIComponent(token)}/aceitar`, {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify(body),
      }),
    contatosContrato: (
      token: string,
      body: {
        assinaturaEmail: string;
        financeiro: { nome: string; email: string };
        notaFiscalEmail?: string;
        implantacao: { nome: string; email: string; telefone: string };
      },
    ): Promise<{ contatosContrato: ContatosContratoAPI }> =>
      request(`/public/propostas/${encodeURIComponent(token)}/contatos-contrato`, {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      }),
    dadosContrato: (
      token: string,
      body: Omit<DadosContratoAPI, 'preenchidoEm'>,
    ): Promise<{
      jaPreenchido: boolean;
      dadosContrato: DadosContratoAPI;
      status: string;
      numero: string;
    }> =>
      request(`/public/propostas/${encodeURIComponent(token)}/dados-contrato`, {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      }),
    /**
     * Consulta Omie pelo CNPJ DA PROPOSTA (não input do cliente).
     * Retorna razão social, endereço etc pra pré-preencher o form de dados-contrato.
     */
    lookupOmie: (
      token: string,
    ): Promise<{
      encontrado: boolean;
      dados: {
        razaoSocial: string;
        cnpj?: string;
        cpf?: string;
        endereco?: {
          rua: string;
          numero: string;
          complemento?: string;
          bairro: string;
          cidade: string;
          uf: string;
          cep: string;
        };
      } | null;
    }> =>
      request(`/public/propostas/${encodeURIComponent(token)}/lookup-omie`, {
        method: 'GET',
        skipAuth: true,
      }),
  },
};
